/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { StrictHttpResponse } from '../strict-http-response';
import { WeatherApiConfiguration } from '../weather-api-configuration';
import { RequestBuilder } from '../request-builder';

import { GetAlerts$Params, getAlerts } from '../fn/reporting/get-alerts';
import { GetAlerts$Csv$Params, getAlerts$Csv } from '../fn/reporting/get-alerts-csv';
import { GetReport$Params, getReport } from '../fn/reporting/get-report';
import { GetReport$Csv$Params, getReport$Csv } from '../fn/reporting/get-report-csv';
import {environment} from '@root/src/environments/environment';

export enum WeatherDataSource {
  Era5 = 1,
  IBM = 2,
  UGM = 3
}

export enum ExportFileType {
  GeoJSON = 1,
  KML = 2,
  Shapefile = 3,
  CSV = 4
}

export interface ExportLayerRequest {
  weatherDataSource: WeatherDataSource;
  layerName: string;
  date: string;
  exportFileType: ExportFileType;
}

export interface ExportPointRequest {
  weatherDataSource: WeatherDataSource;
  latitude: number;
  longitude: number;
  date: string;
  exportFileType: ExportFileType;
}

/**
 * Reporting API
 */
@Injectable({ providedIn: 'root' })
export class ReportingService extends BaseService {
   constructor(config: WeatherApiConfiguration, http: HttpClient) {
      super(config, http);
   }

   /** Path part for operation `getAlerts()` */
   static readonly GetAlertsPath = '/api/weather/reports';

   /**
    * This method provides access to the full `HttpResponse`, allowing access to response headers.
    * To access only the response body, use `getAlerts$Csv()` instead.
    *
    * This method doesn't expect any request body.
    */
   getAlerts$Csv$Response(params?: GetAlerts$Csv$Params, context?: HttpContext): Observable<StrictHttpResponse<any>> {
      return getAlerts$Csv(this.http, this.rootUrl, params, context);
   }

   /**
    * This method provides access only to the response body.
    * To access the full response (for headers, for example), `getAlerts$Csv$Response()` instead.
    *
    * This method doesn't expect any request body.
    */
   getAlerts$Csv(params?: GetAlerts$Csv$Params, context?: HttpContext): Observable<any> {
      return this.getAlerts$Csv$Response(params, context).pipe(map((r: StrictHttpResponse<any>): any => r.body));
   }

   /**
    * This method provides access to the full `HttpResponse`, allowing access to response headers.
    * To access only the response body, use `getAlerts()` instead.
    *
    * This method doesn't expect any request body.
    */
   getAlerts$Response(params?: GetAlerts$Params, context?: HttpContext): Observable<StrictHttpResponse<any>> {
      return getAlerts(this.http, this.rootUrl, params, context);
   }

   /**
    * This method provides access only to the response body.
    * To access the full response (for headers, for example), `getAlerts$Response()` instead.
    *
    * This method doesn't expect any request body.
    */
   getAlerts(params?: GetAlerts$Params, context?: HttpContext): Observable<any> {
      return this.getAlerts$Response(params, context).pipe(map((r: StrictHttpResponse<any>): any => r.body));
   }

   /** Path part for operation `getReport()` */
   static readonly GetReportPath = '/api/weather/reports/{report_id}';

   /**
    * This method provides access to the full `HttpResponse`, allowing access to response headers.
    * To access only the response body, use `getReport$Csv()` instead.
    *
    * This method doesn't expect any request body.
    */
   getReport$Csv$Response(params: GetReport$Csv$Params, context?: HttpContext): Observable<StrictHttpResponse<any>> {
      return getReport$Csv(this.http, this.rootUrl, params, context);
   }

   /**
    * This method provides access only to the response body.
    * To access the full response (for headers, for example), `getReport$Csv$Response()` instead.
    *
    * This method doesn't expect any request body.
    */
   getReport$Csv(params: GetReport$Csv$Params, context?: HttpContext): Observable<any> {
      return this.getReport$Csv$Response(params, context).pipe(map((r: StrictHttpResponse<any>): any => r.body));
   }

   /**
    * This method provides access to the full `HttpResponse`, allowing access to response headers.
    * To access only the response body, use `getReport()` instead.
    *
    * This method doesn't expect any request body.
    */
   getReport$Response(params: GetReport$Params, context?: HttpContext): Observable<StrictHttpResponse<any>> {
      return getReport(this.http, this.rootUrl, params, context);
   }

   /**
    * This method provides access only to the response body.
    * To access the full response (for headers, for example), `getReport$Response()` instead.
    *
    * This method doesn't expect any request body.
    */
   getReport(params: GetReport$Params, context?: HttpContext): Observable<any> {
      return this.getReport$Response(params, context).pipe(map((r: StrictHttpResponse<any>): any => r.body));
   }

   /** Path part for operation `exportLayer()` */
   static readonly ExportLayerPath = '/weatherReports/WeatherReportExport/ExportLayer';
   static readonly ExportLayerFullUrl = `${environment.api}`;

  /** Path part for operation `exportPoint()` */
  static readonly ExportPointPath = '/weatherReports/WeatherReportExport/ExportPoint';
  static readonly ExportPointFullUrl = `${environment.api}`;

   /**
    * Export weather layer data
    *
    * This method provides access to the full `HttpResponse`, allowing access to response headers.
    * To access only the response body, use `exportLayer()` instead.
    *
    * This method sends `application/json` and handles request body of type `application/json`.
    */
   exportLayer$Response(params: { body: ExportLayerRequest }, context?: HttpContext): Observable<StrictHttpResponse<Blob>> {
      const rb = new RequestBuilder(ReportingService.ExportLayerFullUrl, ReportingService.ExportLayerPath, 'post');
      if (params) {
         rb.body(params.body, 'application/json');
      }

      return this.http.request(
         rb.build({ responseType: 'blob', accept: 'application/zip', context })
      ).pipe(
         filter((r: any): r is HttpResponse<Blob> => r instanceof HttpResponse),
         map((r: HttpResponse<Blob>) => {
            return r as StrictHttpResponse<Blob>;
         })
      );
   }

   /**
    * Export weather layer data
    *
    * This method provides access only to the response body.
    * To access the full response (for headers, for example), `exportLayer$Response()` instead.
    *
    * This method sends `application/json` and handles request body of type `application/json`.
    */
   exportLayer(params: { body: ExportLayerRequest }, context?: HttpContext): Observable<HttpResponse<Blob>> {
      return this.exportLayer$Response(params, context).pipe(
         map((r: StrictHttpResponse<Blob>): HttpResponse<Blob> => r as HttpResponse<Blob>)
      );
   }

  /**
   * Export weather point data
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `exportPoint()` instead.
   **/
  exportPoint$Response(params: { body: ExportPointRequest }, context?: HttpContext): Observable<StrictHttpResponse<Blob>> {
    const rb = new RequestBuilder(ReportingService.ExportPointFullUrl, ReportingService.ExportPointPath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(
      rb.build({ responseType: 'blob', accept: 'application/zip', context })
    ).pipe(
      filter((r: any): r is HttpResponse<Blob> => r instanceof HttpResponse),
      map((r: HttpResponse<Blob>) => {
        return r as StrictHttpResponse<Blob>;
      })
    );
  }

  /**
   * Export weather point data
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `exportPoint$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  exportPoint(params: { body: ExportPointRequest }, context?: HttpContext): Observable<HttpResponse<Blob>> {
    return this.exportPoint$Response(params, context).pipe(
      map((r: StrictHttpResponse<Blob>): HttpResponse<Blob> => r as HttpResponse<Blob>)
    );
  }
}
